import Link from 'next/link';
import { useEffect } from 'react';
import { getIntercomAppId } from 'utils/intercom';
import Logo from 'components/generic-components/Logo';
import { isWhiteLabelEnabled } from 'utils/whiteLabelConfig';
import WhiteLabelLoginLayout from './WhiteLabelLoginLayout';

export const AuthContainer = ({ children, verticallyCentered = true }) => {
  return (
    <div className="auth-layout-container relative min-h-full bg-white">
      {/* Absolute (not fixed) → positioned against the page, not the viewport, so it scrolls away
          with the rest of the content instead of staying pinned on top of it once the form scrolls
          under where it sits. Still ignores body's safe-area padding, so add the island inset here. */}
      <div className="visible absolute left-0 top-0 mb-12 mt-[calc(3rem+var(--safe-top))] w-full lg:hidden">
        <Logo className="mx-auto mb-6 h-9" />
      </div>
      {/* Mobile: reserve the fixed logo's space and top-align so the form can't
          rise into the logo when Android shrinks the viewport for the keyboard
          (iOS was fine, but this keeps both consistent and overlap-free).
          Desktop keeps the original vertical centering (logo is hidden there). */}
      <div
        className={`flex min-h-screen flex-col items-center pt-[calc(7rem+var(--safe-top))] lg:pt-0 ${
          verticallyCentered ? 'lg:justify-center' : ''
        }`}
      >
        {children}
      </div>
    </div>
  );
};

export const OnboardingContainer = ({ children }) => {
  return (
    <div className={`auth-layout-container flex min-h-full flex-col items-center bg-white p-4 md:p-12`}>
      <div className="mb-12">
        <Logo className="mx-auto mb-6 h-9" />
      </div>
      <div
        className={`mb-12 min-h-[auto] w-full max-w-md items-center justify-center rounded-xl border border-gray-200 p-4 md:max-w-screen-lg md:p-12 lg:min-w-[1024px]`}
      >
        {children}
      </div>
    </div>
  );
};

const PageLogoTitle = ({ pageTitle, isVerifyEmailPage = false }) => {
  return (
    <>
      <Link href="/" className={`${isVerifyEmailPage ? 'hidden lg:block' : ''}`}>
        <Logo className="mx-auto mb-6 h-9" />
      </Link>
      <h1 className="text-center text-xl font-semibold text-neutral-800" data-testid="pageTitle">
        {pageTitle}
      </h1>
    </>
  );
};

const LoginLayout = ({
  children,
  pageTitle = 'Welcome to Thera',
  showRightContainer = true,
  isAPARSignup = false,
  isVerifyEmailPage = false,
}) => {
  useEffect(() => {
    if (!(window as any).Intercom) return;
    const openIntercom = () => {
      (window as any).Intercom('boot', {
        api_base: 'https://api-iam.intercom.io',
        app_id: getIntercomAppId(),
      });
    };
    openIntercom();
  }, []);

  // Use white label layout when enabled
  if (isWhiteLabelEnabled()) {
    return (
      <WhiteLabelLoginLayout showRightContainer={showRightContainer} isVerifyEmailPage={isVerifyEmailPage}>
        {children}
      </WhiteLabelLoginLayout>
    );
  }

  return (
    <AuthContainer>
      <div className={`mx-auto max-w-md md:max-w-screen-lg`}>
        <div
          className={`w-full rounded-xl bg-white lg:border lg:border-gray-200 ${isVerifyEmailPage ? 'p-4 md:p-12' : 'p-12'}`}
        >
          <div
            className={`${showRightContainer ? 'gap-24 lg:grid lg:max-w-none lg:grid-cols-2 lg:items-center' : 'min-w-sm max-w-sm'} max-w-md`}
          >
            <div>
              {!showRightContainer && (
                <div className={`mb-12 flex flex-col items-center justify-center`}>
                  <PageLogoTitle pageTitle={pageTitle} isVerifyEmailPage={isVerifyEmailPage} />
                </div>
              )}
              {children}
            </div>
            {showRightContainer && (
              <div className="hidden flex-col items-start justify-start lg:flex">
                <PageLogoTitle pageTitle={pageTitle} />
                <div className="mt-6 text-sm font-medium text-neutral-500">
                  {isAPARSignup ? (
                    <>
                      Streamline AP/AR with Thera's world-class platform. Manage and optimize your payments
                      effortlessly.
                    </>
                  ) : (
                    <>
                      One platform. Every financial workflow. Run payroll, pay contractors, manage cash, send invoices,
                      and close your books, all from Thera. Global coverage. Instant onboarding. Zero FX markup.
                    </>
                  )}
                  <br />
                  <br />
                  Built for startups. Scales with enterprises. Whether you're just starting or managing millions,
                  Thera’s ecosystem grows with you.
                  <br />
                  <br />
                  Welcome to the future of business finance. Let’s get started.
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </AuthContainer>
  );
};

export default LoginLayout;
